<script lang="ts">import { goto } from "$app/navigation";
import { Button } from "@infinitro/button";
import { Card } from "@infinitro/card";
import { toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { cfgCdnBase, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { loggedIn, providerBasePath, toCamelCase, toSiteUrl } from "@infinitro/global";
import { ArrowIcon, LocalIcon, RemoteIcon } from "@infinitro/icons";
import { Card as PriceCard } from "@infinitro/price-card";
import { Text } from "@infinitro/text";
import { onDestroy, onMount } from "svelte";
import Swiper from "swiper";
import { Autoplay, FreeMode, Mousewheel, Navigation } from "swiper/modules";
import "swiper/swiper-bundle.css";
// Define CDN base for asset URL generation
const CDN_BASE = cfgCdnBase();
// Exposed props for customization of the component
export let comingSoon = false;
export let data;
export let url;
export let viewAllOnClick = null;
export let title;
export let icon;
export let irregular = true;
export let padding = "";
export let CustomCard = null;
export let cardWidth = "";
export let cardHeight = "";
export let spaceBetween = 8;
export let slidesPerView = null;
export let customSlidesPerView = {};
export let noViewAllLink = false;
export let noCardLink = false;
export let minHeight = false;
export let customStyle = "";
export let customStyleSwiperContainer = "";
export let centerSlides = false;
export let centerIndex = 0;
export let titleNotClickable = false;
export let centerIndexReady = false;
export let callFromHomePage = false;
export let touchRatio = 1;
export let freeModeMomentumRatio = .5;
export let freeModeMomentumVelocityRatio = .5;
// Calculate initial visible slides count based on container width
let visibleSlidesCount = 0;
// Sanitizing the title for use in dynamic IDs and other elements
let sanitizedTitle = title?.replace(/[^a-zA-Z0-9]/g, "-");
let nextButtonId = `id-next-${sanitizedTitle}`;
let prevButtonId = `id-prev-${sanitizedTitle}`;
// Mounted flag to track component lifecycle
let mounted = false;
// Declare swiper instance and related DOM elements
let swiper;
let swiperContainer;
let containerRef;
let swiperHeight = "107px";
let startX = 0;
let startY = 0;
// Prevent browser back gesture by detecting horizontal swipes
const handleTouchStart = (e) => {
	startX = e.touches[0].clientX;
	startY = e.touches[0].clientY;
};
const handleTouchMove = (e) => {
	const deltaX = e.touches[0].clientX - startX;
	const deltaY = e.touches[0].clientY - startY;
	// If horizontal swipe detected, prevent default to block browser back
	if (Math.abs(deltaX) > Math.abs(deltaY)) {
		e.preventDefault();
	}
};
// Initialize Swiper instance with custom configurations
const initializeSwiper = () => {
	// Destroy existing Swiper instance before initializing a new one
	if (swiper) {
		swiper.destroy(true, true);
	}
	// Configure breakpoints for responsive slides per view
	const breakpointsConfig = Object.keys(customSlidesPerView).length ? Object.entries(customSlidesPerView).reduce((acc, [breakpoint, slides]) => {
		acc[breakpoint] = { slidesPerView: slides };
		return acc;
	}, {}) : {
		200: { slidesPerView: slidesPerView || 3.2 },
		640: { slidesPerView: slidesPerView || 4.2 },
		720: { slidesPerView: slidesPerView || 6 },
		1280: { slidesPerView: slidesPerView || 8 }
	};
	// Initialize Swiper with navigation and autoplay modules
	swiper = new Swiper(swiperContainer, {
		modules: [
			Navigation,
			Autoplay,
			Mousewheel,
			FreeMode
		],
		loop: false,
		loopAddBlankSlides: false,
		navigation: {
			nextEl: `#${nextButtonId}`,
			prevEl: `#${prevButtonId}`
		},
		spaceBetween,
		slidesOffsetBefore: 0,
		mousewheel: { forceToAxis: true },
		freeMode: {
			enabled: true,
			momentum: true,
			momentumRatio: freeModeMomentumRatio,
			momentumVelocityRatio: freeModeMomentumVelocityRatio
		},
		touchRatio,
		grabCursor: true,
		on: { init: () => {
			document.getElementById(nextButtonId)?.addEventListener("click", moveNextSlides);
			document.getElementById(prevButtonId)?.addEventListener("click", movePrevSlides);
		} },
		breakpoints: breakpointsConfig
	});
	if (centerSlides) {
		const centerOffset = Math.floor(getSlidesPerView() / 2);
		if (centerIndex) {
			centerIndex = Math.max(centerIndex - centerOffset, 0);
		} else {
			centerIndex = Math.max(Math.ceil(data.length / 2) - centerOffset, 0);
		}
		// console.log(
		// 	`centerIndex: ${centerIndex} centerOffset: ${centerOffset} view ${getSlidesPerView()}`
		// );
		try {
			swiper?.slideTo?.(centerIndex, 0);
		} catch (error) {
			console.error("Error sliding to center index", error);
		}
	}
};
// Calculate the number of visible slides based on the container's width and swiper settings
const getVisibleSlidesCount = () => {
	const slides = swiper.slides;
	const containerWidth = swiperContainer.clientWidth;
	let visibleWidth = 0;
	let visibleSlides = 0;
	if (!slides || slides.length === 0) return 0;
	// Calculate how many slides fit in the container based on the available space
	for (let i = swiper.activeIndex; i < slides.length; i++) {
		visibleWidth += slides[i].clientWidth + Number(swiper.params.spaceBetween);
		if (visibleWidth > containerWidth) break;
		visibleSlides++;
	}
	return visibleSlides;
};
// Move to the next set of slides based on visible slides count
const moveNextSlides = () => {
	const visibleSlides = getVisibleSlidesCount();
	const newIndex = swiper.activeIndex + visibleSlides;
	try {
		swiper?.slideTo?.(newIndex - 1);
	} catch (error) {
		console.error("Error moving to next slides", error);
	}
};
// Move to the previous set of slides based on visible slides count
const movePrevSlides = () => {
	const visibleSlides = getVisibleSlidesCount();
	const newIndex = swiper.activeIndex - visibleSlides;
	try {
		swiper?.slideTo?.(newIndex - 1);
	} catch (error) {
		console.error("Error moving to previous slides", error);
	}
};
// Calculate the height of the swiper container based on breakpoints and container width
function getSwiperHeight() {
	const aspectRatio = 236 / 176;
	const containerWidth = containerRef?.clientWidth || window.innerWidth;
	const breakpointsConfig = {
		200: 3.2,
		640: 4.2,
		720: 6,
		1280: 8
	};
	let slidesPerView = 3.2;
	// Determine slidesPerView based on the container's width
	for (const [breakpoint, slides] of Object.entries(breakpointsConfig)) {
		if (containerWidth >= Number(breakpoint)) {
			slidesPerView = slides;
		}
	}
	const spaceBetweenPx = spaceBetween || 8;
	const slideWidth = (containerWidth - spaceBetweenPx * (slidesPerView - 1)) / slidesPerView;
	return `${slideWidth * aspectRatio}px`;
}
// Method to center the today reward if the content type are rewards
const getTodayRewardIndex = () => {
	if (!data || !data.length) return 0;
	const today = new Date().setHours(0, 0, 0, 0);
	const todayIndex = data.findIndex((reward) => new Date(reward.date).setHours(0, 0, 0, 0) === today);
	return todayIndex !== -1 ? todayIndex : 0;
};
const getSlidesPerView = () => {
	const containerWidth = swiperContainer.clientWidth;
	let slidesPerView = 3;
	for (const breakpoint of Object.keys(customSlidesPerView).map(Number).sort((a, b) => a - b)) {
		if (containerWidth >= breakpoint) {
			slidesPerView = customSlidesPerView[breakpoint];
		}
	}
	return slidesPerView;
};
// Calculate visible slides count based on screen size (not container)
const calculateInitialVisibleSlides = () => {
	const screenWidth = window.innerWidth;
	// Mobile: 4 slides, Tablet: 6 slides, Desktop: 8 slides
	if (screenWidth >= 1280) {
		return 8;
	} else if (screenWidth >= 720) {
		return 6;
	} else {
		return 4;
	}
};
const gotoLink = () => {
	const link = toSiteUrl(url, $loggedIn) || "#";
	if (link !== "#") {
		goto(link);
	}
};
// Subscribe to the locale store when the component is mounted
onMount(() => {
	mounted = true;
	// Attach touch handlers to prevent browser back gesture
	if (swiperContainer) {
		swiperContainer.addEventListener("touchstart", handleTouchStart, { passive: true });
		swiperContainer.addEventListener("touchmove", handleTouchMove, { passive: false });
	}
});
// Clean up the subscriptions and swiper instance when the component is destroyed
onDestroy(() => {
	if (swiper) swiper.destroy(true, true);
	// Remove touch event listeners
	if (swiperContainer) {
		swiperContainer.removeEventListener("touchstart", handleTouchStart);
		swiperContainer.removeEventListener("touchmove", handleTouchMove);
	}
});
// Reactively update swiper height and visible slides count when the container ref is available
$: {
	if (containerRef) {
		swiperHeight = getSwiperHeight();
		visibleSlidesCount = calculateInitialVisibleSlides();
	}
}
// Initialize the Swiper instance when the swiper container is available and mounted
$: if (swiperContainer && mounted && (!centerSlides || centerIndexReady)) {
	initializeSwiper();
}
$: sectionIconSize = $stateUi.layout.sectionIconSize || 24;
$: useAlternative4 = $stateUi.cardSwiper.cardSwiperStyle === "alternative4";
$: viewAllAsButton = $stateUi.cardSwiper.viewAllAsButton ?? false;
$: showNavigationArrows = $stateUi.cardSwiper.showNavigationArrows ?? true;
$: disableEdgeMask = $stateUi.cardSwiper.disableEdgeMask ?? false;
$: useRemoteIcons = $stateUi.cardSwiper.useRemoteIcons ?? false;
</script>

{#if mounted}
	<div class="card-swiper-section" style={customStyle ? customStyle : ''}>
		{#if title}
			<div
				class:header-container={$stateUi.layout.useReducedMarginsInHomePage === true}
				class:header-container-alternative={$stateUi.layout.useReducedMarginsInHomePage === false}
			>
				<div class="title-container">
					{#if icon && typeof icon === 'string'}
						<div class="title-icon">
							{#if $stateUi.layout.useRemoteIcons === true}
								<RemoteIcon {icon} size={sectionIconSize} />
							{:else}
								<img src={icon} alt={title} />
							{/if}
						</div>
					{/if}
					{#if icon && typeof icon === 'function'}
						<div class="title-icon">
							<svelte:component this={icon} />
						</div>
					{/if}
					<div class="title-desktop" class:semibold={callFromHomePage}>
						{#if !titleNotClickable}
							<a href={toSiteUrl(url, $loggedIn) || '#'} class="view-all-link">
								<Text type="subtitle" subtitleSize="large" subtitleWeight="semibold">{title}</Text>
							</a>
						{:else}
							<Text type="subtitle" subtitleSize="large" subtitleWeight="semibold">{title}</Text>
						{/if}
					</div>
					<div class="title-mobile" class:semibold={callFromHomePage}>
						{#if !titleNotClickable}
							<a href={toSiteUrl(url, $loggedIn) || '#'} class="view-all-link">
								<Text type="subtitle" subtitleSize="small" subtitleWeight="semibold">{title}</Text>
							</a>
						{:else}
							<Text type="subtitle" subtitleSize="small" subtitleWeight="semibold">{title}</Text>
						{/if}
					</div>
					<slot name="title-extra" />
				</div>
				{#if !comingSoon}
					<div class="navigation-container">
						{#if !noViewAllLink && viewAllOnClick}
							<button on:click={viewAllOnClick} class="view-all-link"
								>{`${$t(Verbiage.viewAll)}`}</button
							>
						{:else if !noViewAllLink}
							{#if viewAllAsButton}
								<Button
									variant="secondary"
									className="link-navigation-alternative"
									size="small"
									text={`${$t(Verbiage.viewAll)}`}
									onClick={gotoLink}
								/>
							{:else}
								<a
									href={toSiteUrl(url, $loggedIn) || '#'}
									class="view-all-link"
									class:view-all-link-alternative-4={useAlternative4}>{`${$t(Verbiage.viewAll)}`}</a
								>
							{/if}
						{/if}
						{#if showNavigationArrows}
							{#if false}
								<div class="total-badge">
									<span class="total-badge-text">{data?.length}</span>
								</div>
							{/if}
							<div class="button-container">
								<div
									id={prevButtonId}
									class="swiper-button-prev"
									class:swiper-button-prev-alternative-4={useAlternative4}
								>
									{#if useRemoteIcons}
										<RemoteIcon icon="ArrowLeftIcon" size={22} />
									{:else if useAlternative4}
										<span>
											<LocalIcon icon="ArrowIcon" />
										</span>
									{/if}
								</div>
								<div
									id={nextButtonId}
									class="swiper-button-next"
									class:swiper-button-next-alternative-4={useAlternative4}
								>
									{#if useRemoteIcons}
										<RemoteIcon icon="ArrowRightIcon" size={22} />
									{:else if useAlternative4}
										<span>
											<LocalIcon icon="ArrowIcon" />
										</span>
									{/if}
								</div>
							</div>
						{/if}
					</div>
				{/if}
			</div>
		{/if}
		{#if comingSoon}
			<img
				src={`${CDN_BASE}/banner/coming-soon-banner.png`}
				alt={$t(Verbiage.icon_alt, 'alts')}
				class="coming-soon-banner"
			/>
			<img
				src={`${CDN_BASE}/banner_m/coming-soon-banner-mobile.png`}
				alt={$t(Verbiage.icon_alt, 'alts')}
				class="coming-soon-banner-mobile"
			/>
		{:else if data && data.length > 0}
			<div
				class="swiper card-swiper-container
				{irregular ? 'card-swiper-container-irregular' : ''}"
				class:disable-edge-mask={disableEdgeMask}
				bind:this={swiperContainer}
				style="padding: {padding}; {customStyleSwiperContainer}"
			>
				<div class="swiper-wrapper">
					{#each data as item, index (`${index}-${item.key ?? `${item.game ?? ''}-${item.prov ?? ''}`}-${$loggedIn}`)}
						{#if CustomCard}
							<div class="swiper-slide">
								{#if noCardLink}
									<svelte:component this={CustomCard} {item} id={item.id} />
								{:else}
									<a
										href={item.page ? toSiteUrl(`/${item.page}/`, $loggedIn) || '#' : '#'}
										class="swiper-card-link"
									>
										<svelte:component this={CustomCard} {item} id={item.id} />
									</a>
								{/if}
							</div>
						{:else if 'price' in item}
							<div class="swiper-slide">
								<PriceCard {item} />
							</div>
						{:else if item.game && item.prov}
							{#if item.notAvailable}
								<div id={toCamelCase(item.game)} class="swiper-slide">
									<Card
										{item}
										eagerLoad={index < visibleSlidesCount}
										width={cardWidth}
										height={cardHeight}
									/>
								</div>
							{:else}
								<a
									id={toCamelCase(item.game)}
									href={toSiteUrl(
										`${providerBasePath(item.prov)}${toProviderSlug(item.prov)}/${item.prov?.toLowerCase() === 'sports' ? item.game : toGameSlug(item.game)}`,
										$loggedIn
									) || '#'}
									class="swiper-slide"
								>
									<Card
										{item}
										eagerLoad={index < visibleSlidesCount}
										width={cardWidth}
										height={cardHeight}
									/>
								</a>
							{/if}
						{:else}
							<a
								id={toCamelCase(item.game)}
								href={toSiteUrl(`/provider/${item.page}/`, $loggedIn) || '#'}
								class="swiper-slide"
							>
								<Card
									{item}
									eagerLoad={index < visibleSlidesCount}
									width={cardWidth}
									height={cardHeight}
								/>
							</a>
						{/if}
					{/each}
				</div>
			</div>
		{:else}
			<div class="swiper-placeholder" style="height: {swiperHeight};"></div>
		{/if}
	</div>
{:else}
	<div class="container-ref" bind:this={containerRef}></div>
{/if}

<style>
	:global(.link-navigation-alternative) {
		border-width: 0px !important;
}

	.container-ref {
		width: 100%;
}

	.swiper-placeholder {
		width: 100%;
		background-color: transparent;
}

	.card-swiper-section {
		position: relative;
		display: flex;
		width: 100%;
		flex-direction: column;
		overflow: visible;
		font-family: var(--font-main);
}

	.card-swiper-container {
		position: relative;
		height: auto;
		width: 100%;
		overflow: hidden;
		padding-top: calc(0.25rem * 3);
		padding-bottom: calc(0.25rem * 6);
		overscroll-behavior-x: contain;
}

	.card-swiper-container-irregular {
		width: calc(100% + 14px);
		margin-left: -7px;
		padding-left: 7px;
		padding-right: 7px;
	}

	@media (max-width: 719px) {
		.card-swiper-container-irregular {
			padding-right: 0;
			width: calc(100% + 28px);
		}
	}

	.card-swiper-container .swiper-slide {
		display: flex;
		width: -moz-fit-content;
		width: fit-content;
		justify-content: center;
		overflow: visible;
		opacity: 1;
}

	.header-container {
		margin-bottom: 0px;
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.header-container-alternative {
		margin-bottom: 0px;
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	@media (min-width: 1024px) {

		.header-container-alternative {
				margin-bottom: 0.75rem;
		}
}

	.title-container {
		display: flex;
		align-items: center;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	/*.title-container img, */
	.title-icon {
		margin-right: 0.25rem;
		display: flex;
		height: 1.5rem;
		align-items: center;
		justify-content: center;
}
	@media (min-width: 1024px) {

		.title-icon {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container img, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container img, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container svg, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container svg, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container > svg, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container > svg, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	.title {
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.title {
				font-size: 1.5rem;
				line-height: 2rem;
		}
}

	.title-desktop {
		display: none;
}

	@media (min-width: 1024px) {

		.title-desktop {
				display: block;
		}
}

	.title-mobile {
		display: block;
}

	@media (min-width: 1024px) {

		.title-mobile {
				display: none;
		}
}

	.navigation-container {
		display: flex;
		align-items: center;
		gap: calc(0.25rem * 2);
}

	.view-all-link {
		background-color: transparent;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.view-all-link {
				font-size: 14px;
		}
}

	.view-all-link:hover {
		cursor: pointer;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.view-all-link-alternative-4 {
		display: flex;
		height: 32px;
		align-items: center;
		border-radius: 0.5rem;
		background-color: var(--color-secondary-500);
		padding-left: 1rem;
		padding-right: 1rem;
}

	.button-container {
		display: flex;
		height: 100%;
		align-items: center;
}

	.total-badge {
		margin-left: 0.5rem;
		border-radius: 6px;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 800;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		background: var(--gradient-primary);
}

	:global(.button-container .swiper-button-prev, .button-container .swiper-button-next) {
		position: relative;
		z-index: 99;
		margin-left: 0.75rem;
		margin-top: 0px;
		height: 2rem;
		width: 2rem;
		transform: none;
		cursor: pointer;
		border-radius: 0.5rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		outline: 2px solid transparent;
		outline-offset: 2px;
		background: var(--gradient-alternative-2);
		position: static !important;
		margin-top: 0 !important;
		height: 32px !important;
		width: 32px !important;
}

	:global(
		.button-container .swiper-button-prev:hover,
		.button-container .swiper-button-next:hover
	) {
		border-top-width: 2px;
		border-style: none;
		background: var(--gradient-alternative-2);
}

	.swiper-button-prev-alternative-4,
	.swiper-button-next-alternative-4 {
		border-width: 0px;
		background-color: var(--color-secondary-500);
		background-image: unset;
}

	.swiper-button-prev-alternative-4 > span,
	.swiper-button-next-alternative-4 > span {
		display: inline-block;
		height: 1rem;
		width: 1rem;
}

	.swiper-button-prev-alternative-4 > span {
		--tw-rotate: 90deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.swiper-button-next-alternative-4 > span {
		--tw-rotate: -90deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.swiper-button-prev-alternative-4::after,
	.swiper-button-next-alternative-4::after {
		content: '';
	}

	:global(
		.button-container .swiper-button-prev::after,
		.button-container .swiper-button-next::after
	) {
		font-size: 8px !important;
		color: #c2cdff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 900;
	}

	@media (max-width: 1023px) {
		:global(.button-container .swiper-button-prev, .button-container .swiper-button-next) {
			position: static !important;
			transform: none;
			margin-left: 5px;
			margin-top: 0 !important;
		}
	}

	.coming-soon-banner {
		display: none;
}

	@media (min-width: 1024px) {

		.coming-soon-banner {
				display: block;
		}
}

	.coming-soon-banner-mobile {
		display: block;
}

	@media (min-width: 1024px) {

		.coming-soon-banner-mobile {
				display: none;
		}
}

	.card-swiper-container {
		position: relative;
		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0%,
			black 4%,
			black 92%,
			transparent 98%
		);
		mask-image: linear-gradient(to right, transparent 0%, black 4%, black 92%, transparent 98%);
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
		padding-top: calc(0.25rem * 3);
		padding-bottom: calc(0.25rem * 6);
	}

	@media (min-width: 1024px) {
		.card-swiper-container {
			-webkit-mask-image: linear-gradient(
				to right,
				transparent 0%,
				black 1.5%,
				black 98.5%,
				transparent 99.5%
			);
			mask-image: linear-gradient(
				to right,
				transparent 0%,
				black 1.5%,
				black 98.5%,
				transparent 99.5%
			);
			-webkit-mask-repeat: no-repeat;
			mask-repeat: no-repeat;
			padding-top: calc(0.25rem * 3);
			padding-bottom: calc(0.25rem * 6);
		}
	}

	.disable-edge-mask {
		mask-image: none;
		-webkit-mask-image: none;
	}

	.semibold {
		font-weight: 600;
}

	.swiper-card-link {
		display: block;
		height: 100%;
		width: 100%;
}
</style>
